import { useEffect, useRef } from 'react'
import { HIDE_ERRORS } from '@/lib/config'
import {
    type NoticeAction,
    type NoticeIcon,
    type NoticeInput,
    type Severity,
    useNoticeStore,
} from '@/stores/notice-store'

export type { NoticeAction, NoticeIcon, NoticeInput, Severity }

export const notice = {
    show: (input: NoticeInput): string => useNoticeStore.getState().show(input),
    dismiss: (id: string): void => useNoticeStore.getState().dismiss(id),
    dismissAll: (): void => useNoticeStore.getState().dismissAll(),
}

type NotifyOptions = Omit<NoticeInput, 'severity' | 'message'>

const DEFAULT_DURATION = {
    success: 3000,
    info: 3000,
    warning: 4000,
    error: 5000,
} as const

function makeNotifier(severity: Severity, defaultDuration: number) {
    return (message: string, opts?: NotifyOptions): string =>
        useNoticeStore.getState().show({
            duration: defaultDuration,
            ...opts,
            severity,
            message,
        })
}

// No notify.neutral: 'neutral' severity is for persistent notices (e.g. impersonation banner)
// shown via useNoticeWhile, not for ephemeral toasts.
export const notify = {
    success: makeNotifier('success', DEFAULT_DURATION.success),
    info: makeNotifier('info', DEFAULT_DURATION.info),
    warning: makeNotifier('warning', DEFAULT_DURATION.warning),
    error: (message: string, opts?: NotifyOptions): string | null => {
        if (HIDE_ERRORS) return null
        return makeNotifier('error', DEFAULT_DURATION.error)(message, opts)
    },
}

function serializeForDeps(input: NoticeInput & { id: string }) {
    const { actions, icon, ...rest } = input
    return {
        ...rest,
        actions: actions?.map(a => ({ label: a.label, dismissOnPress: a.dismissOnPress })),
        icon: typeof icon === 'string' ? icon : icon ? 'fn' : undefined,
    }
}

export function useNoticeWhile(condition: boolean, input: NoticeInput & { id: string }): void {
    const lastShownIdRef = useRef<string | null>(null)

    // biome-ignore lint/correctness/useExhaustiveDependencies: input is folded into a stringified dep via serializeForDeps; effect closes over input directly.
    useEffect(() => {
        if (!condition) {
            const lastId = lastShownIdRef.current
            if (lastId) {
                useNoticeStore.getState().dismiss(lastId)
                lastShownIdRef.current = null
            }
            return
        }

        const prevId = lastShownIdRef.current
        if (prevId && prevId !== input.id) {
            useNoticeStore.getState().dismiss(prevId)
        }

        useNoticeStore.getState().show(input)
        lastShownIdRef.current = input.id

        return () => {
            if (lastShownIdRef.current !== input.id) return
            useNoticeStore.getState().dismiss(input.id)
            lastShownIdRef.current = null
        }
    }, [condition, JSON.stringify(serializeForDeps(input))])
}
